import React, { useState, useRef } from 'react';
import Modal from './ModalConversion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileExcel, faCloudUploadAlt, faCheckCircle, faTimesCircle, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { urlPod, endpointUploadIngramExcel } from '../services/Endpoints';
import AuthService from '../services/AuthService';
import { theme } from '../theme';

const IngramExcelModal = ({ isOpen, onClose, onSuccess }) => {
    const [file, setFile] = useState(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null); // { updated, not_found }
    const [error, setError] = useState(null);
    const inputRef = useRef(null);

    const resetState = () => {
        setFile(null);
        setResult(null);
        setError(null);
        setUploading(false);
    };

    const handleClose = () => {
        resetState();
        onClose();
    };

    const handleFileSelect = (selectedFile) => {
        setError(null);
        setResult(null);
        if (!selectedFile) return;
        if (!selectedFile.name.toLowerCase().endsWith('.xlsx')) {
            setError('Solo se aceptan archivos .xlsx');
            return;
        }
        setFile(selectedFile);
    };

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const droppedFile = e.dataTransfer.files[0];
        handleFileSelect(droppedFile);
    };

    const handleUpload = async () => {
        if (!file) return;
        const token = AuthService.getToken();
        if (!token) {
            setError('No autorizado. Por favor inicia sesión.');
            return;
        }
        setUploading(true);
        setError(null);
        setResult(null);
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await fetch(`${urlPod}${endpointUploadIngramExcel}`, {
                method: 'POST',
                headers: {
                    'Authorization': token,
                    'Lang': 'es',
                },
                body: formData,
            });
            const res = await response.json();
            if (res && res.status === 'success') {
                setResult(res.data);
                if (onSuccess) onSuccess();
            } else {
                setError('Error al procesar el archivo. Revisa el formato e intenta de nuevo.');
            }
        } catch (err) {
            setError('Error de conexión. Intenta más tarde.');
        } finally {
            setUploading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose}>
            <div className="p-2">
                <h3 className="mb-1 text-white">
                    <FontAwesomeIcon icon={faFileExcel} className="me-2" style={{ color: '#4ade80' }} />
                    Carga Masiva Ingram
                </h3>
                <p className="text-white-50 small mb-4">
                    Sube un archivo <strong>.xlsx</strong> con las columnas <code>isbn_or_sku</code>, <code>status</code> (on/off) y opcionalmente <code>date</code> (YYYY-MM-DD).
                </p>

                {/* Drop zone */}
                <div
                    onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={handleDrop}
                    onClick={() => inputRef.current?.click()}
                    style={{
                        border: `2px dashed ${dragging ? theme.primary : file ? theme.primary : theme.secondary}`,
                        borderRadius: '12px',
                        padding: '2.5rem',
                        textAlign: 'center',
                        cursor: 'pointer',
                        transition: 'border-color 0.2s, background 0.2s',
                        background: dragging ? `rgba(\${theme.primaryRgb}, 0.05)` : file ? `rgba(\${theme.primaryRgb}, 0.05)` : 'rgba(var(--ink-overlay-rgb),0.02)',
                        marginBottom: '1.5rem',
                    }}
                >
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".xlsx"
                        style={{ display: 'none' }}
                        onChange={(e) => handleFileSelect(e.target.files[0])}
                    />
                    <FontAwesomeIcon
                        icon={file ? faFileExcel : faCloudUploadAlt}
                        style={{ fontSize: '2.5rem', color: file ? theme.primary : theme.secondary, marginBottom: '0.75rem' }}
                    />
                    {file ? (
                        <p className="text-white m-0" style={{ fontWeight: 500 }}>
                            {file.name}
                            <span className="ms-2 text-white-50 small">
                                ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                        </p>
                    ) : (
                        <>
                            <p className="text-white-50 m-0">Arrastra tu archivo aquí o haz clic para seleccionar</p>
                            <p className="text-white-50 small m-0 mt-1">Solo archivos .xlsx</p>
                        </>
                    )}
                </div>

                {/* Error message */}
                {error && (
                    <div className="d-flex align-items-center gap-2 mb-3 p-3 rounded"
                        style={{ background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)' }}>
                        <FontAwesomeIcon icon={faTimesCircle} style={{ color: '#f87171' }} />
                        <span className="text-white-50 small">{error}</span>
                    </div>
                )}

                {/* Success result */}
                {result && (
                    <div className="mb-3 p-3 rounded"
                        style={{ background: 'rgba(74,222,128,0.08)', border: '1px solid rgba(74,222,128,0.3)' }}>
                        <div className="d-flex align-items-center gap-2 mb-2">
                            <FontAwesomeIcon icon={faCheckCircle} style={{ color: '#4ade80' }} />
                            <span className="text-white fw-bold">Carga completada</span>
                        </div>
                        <div className="text-white-50 small">
                            <p className="m-0">✅ Títulos actualizados: <strong className="text-white">{result.updated}</strong></p>
                            {result.not_found?.length > 0 && (
                                <p className="m-0 mt-1">⚠️ No encontrados: <strong className="text-warning">{result.not_found.length}</strong>
                                    <span className="ms-1">({result.not_found.slice(0, 5).join(', ')}{result.not_found.length > 5 ? '...' : ''})</span>
                                </p>
                            )}
                        </div>
                    </div>
                )}

                {/* Format hint */}
                <div className="p-3 rounded mb-4"
                    style={{ background: 'rgba(var(--ink-overlay-rgb),0.03)', border: '1px solid rgba(var(--ink-overlay-rgb),0.08)' }}>
                    <p className="text-white-50 small m-0 fw-bold mb-1">Formato esperado del Excel:</p>
                    <table className="w-100" style={{ fontSize: '0.75rem', color: 'var(--ink-text-dim)' }}>
                        <thead>
                            <tr>
                                <th style={{ padding: '4px 8px', borderBottom: '1px solid rgba(var(--ink-overlay-rgb),0.1)', color: 'var(--ink-text-secondary)' }}>isbn_or_sku</th>
                                <th style={{ padding: '4px 8px', borderBottom: '1px solid rgba(var(--ink-overlay-rgb),0.1)', color: 'var(--ink-text-secondary)' }}>status</th>
                                <th style={{ padding: '4px 8px', borderBottom: '1px solid rgba(var(--ink-overlay-rgb),0.1)', color: 'var(--ink-text-secondary)' }}>date (opcional)</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr>
                                <td style={{ padding: '4px 8px' }}>9780123456789</td>
                                <td style={{ padding: '4px 8px' }}>on</td>
                                <td style={{ padding: '4px 8px' }}>2026-07-20</td>
                            </tr>
                            <tr>
                                <td style={{ padding: '4px 8px' }}>9780987654321</td>
                                <td style={{ padding: '4px 8px' }}>off</td>
                                <td style={{ padding: '4px 8px' }}></td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                {/* Actions */}
                <div className="d-flex justify-content-end gap-3 pt-3 border-top border-secondary">
                    <button
                        type="button"
                        className="btn btn-outline-secondary px-4"
                        onClick={handleClose}
                        disabled={uploading}
                    >
                        Cerrar
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary px-4 d-flex align-items-center gap-2"
                        onClick={handleUpload}
                        disabled={!file || uploading || !!result}
                        style={{ border: 'none' }}
                    >
                        {uploading
                            ? <><FontAwesomeIcon icon={faSpinner} spin /> Procesando...</>
                            : <><FontAwesomeIcon icon={faCloudUploadAlt} /> Subir y actualizar</>
                        }
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default IngramExcelModal;
